import React from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * DashboardSectionCard — premium uniform wrapper used across all dashboard sections.
 *
 * Spec:
 *  - background:  surface (#0A0C12 family)
 *  - border:      1px solid rgba(255,255,255,0.10)
 *  - radius:      24px (rounded-3xl)
 *  - padding:     20px (p-5)
 *  - overflow:    hidden
 *
 * Vertical spacing between cards is handled by the parent grid (gap-6 = 24px).
 */
export default function DashboardSectionCard({
  icon: Icon,
  title,
  iconClass = "text-muted-foreground",
  count,
  countClass,
  right,
  className,
  contentClassName,
  footer,
  children,
  testid,
}) {
  return (
    <section
      className={cn(
        "flex flex-col h-full min-h-0 w-full min-w-0 box-border rounded-[22px] border border-border bg-card pt-[22px] px-5 pb-[18px] overflow-hidden",
        "shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_rgba(15,23,42,0.06)] dark:shadow-none",
        "transition-colors duration-200",
        className
      )}
      data-testid={testid}
      aria-label={typeof title === "string" ? title : undefined}
    >
      <header className="shrink-0 flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <Icon className={cn("h-4 w-4 shrink-0", iconClass)} />}
          <h2 className="text-[11px] uppercase tracking-[0.18em] font-semibold text-foreground/85 truncate">
            {title}
          </h2>
          {typeof count === "number" && count > 0 && (
            <Badge
              variant="outline"
              className={cn(
                "text-[10px] h-5 px-1.5 ml-1 border tabular-nums",
                countClass || "border-border text-muted-foreground"
              )}
            >
              {count}
            </Badge>
          )}
        </div>
        {right && <div className="shrink-0 flex items-center gap-2">{right}</div>}
      </header>
      <div className={cn("flex-1 min-h-0 flex flex-col", contentClassName)}>{children}</div>
      {footer && <footer className="shrink-0 mt-auto pt-4">{footer}</footer>}
    </section>
  );
}
